import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [clientProfile, setClientProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Helper function to check if user has complete address
  const hasCompleteAddress = (userData) => {
    return userData && 
           userData.address && 
           userData.city && 
           userData.zipCode &&
           userData.state;
  };

  useEffect(() => {
    if (token) {
      fetchUserProfile(token);
    } else {
      setLoading(false);
    }
  }, [token]); // Re-run when token changes

  const fetchClientProfile = async (authToken) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/profile`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (response.data && response.data.clientProfile) {
        console.log('AuthContext - Client profile fetched:', response.data.clientProfile);
        setClientProfile(response.data.clientProfile);
      } else {
        console.log('AuthContext - No client profile found (user may not have one yet)');
        setClientProfile(null);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        console.log('AuthContext - No client profile found (new user)');
        setClientProfile(null);
      } else {
        console.error('Error fetching client profile:', error);
      }
    }
  };

  const fetchUserProfile = async (authToken) => {
    try {
      console.log('Fetching user profile with token:', authToken);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/profile`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      console.log('AuthContext - Raw profile response:', response.data);
      
      // Ensure we have all required address fields
      const userData = {
        ...response.data,
        address: response.data.address || '',
        city: response.data.city || '',
        state: response.data.state || '',
        zipCode: response.data.zipCode || ''
      };
      
      if (process.env.NODE_ENV === 'development') {
        console.log('AuthContext - Processed user data:', userData);
        console.log('AuthContext - User role from profile:', userData?.role);
        console.log('AuthContext - Has complete address:', hasCompleteAddress(userData));
      }
      
      setUser(userData);
      
      // Fetch client profile after user profile is loaded
      await fetchClientProfile(authToken);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      // Only remove token if it's an auth error
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
        email,
        password,
        tenantId: process.env.REACT_APP_TENANT_ID || '1',
        cityCode: process.env.REACT_APP_CITY_CODE || 'MCO'
      });
      const { token: newToken, user: basicUser } = response.data;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('AuthContext - Login response basicUser:', basicUser);
        console.log('AuthContext - User role from login:', basicUser?.role);
      }
      
      // Store token and trigger profile fetch
      localStorage.setItem('token', newToken);
      setToken(newToken); // This will trigger the useEffect to fetch the complete profile
      
      // Set basic user data while profile loads
      setUser(basicUser);
      setError(null);
      
      // Ensure role information is available for immediate navigation decisions
      if (!basicUser?.role) {
        console.warn('AuthContext - No role information in basic user data');
      }
      
      return {
        success: true,
        user: basicUser
      };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'An error occurred during login';
      setError(errorMessage);
      
      // Enhanced error logging for debugging
      if (process.env.NODE_ENV === 'development') {
        console.error('AuthContext - Login error:', {
          status: error.response?.status,
          error: errorMessage,
          fullError: error.response?.data
        });
      }
      
      // Return error details for component handling
      return {
        success: false,
        error: errorMessage,
        status: error.response?.status
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/register`, userData);
      const { token: newToken, user: basicUser } = response.data;
      
      // Store token and trigger profile fetch
      localStorage.setItem('token', newToken);
      setToken(newToken);
      
      // Set basic user data while profile loads
      setUser(basicUser);
      setError(null);
      return basicUser;
    } catch (error) {
      setError(error.response?.data?.error || 'An error occurred during registration');
      throw error;
    }
  };

  const logout = (navigate) => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setClientProfile(null);
    if (navigate) navigate('/');
  };

  const updateProfile = async (profileData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/auth/profile`,
        profileData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setUser(response.data);
      setError(null);
      return response.data;
    } catch (error) {
      setError(error.response?.data?.error || 'An error occurred while updating profile');
      throw error;
    }
  };

  const createUser = async (userData) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/create-user`, userData);
      const { token, user: basicUser } = response.data;
      
      // Store token and trigger profile fetch for complete user data
      localStorage.setItem('token', token);
      setToken(token); // This will trigger the useEffect to fetch the complete profile
      
      // Set basic user data while profile loads
      setUser(basicUser);
      setError(null);
      return basicUser;
    } catch (error) {
      setError(error.response?.data?.error || 'An error occurred during account creation');
      throw error;
    }
  };

  // Helper functions for client profile
  const getClientProfile = () => clientProfile;
  const hasClientProfile = () => !!clientProfile;
  const isReturningCustomer = () => {
    return clientProfile && (
      clientProfile.requiredServices?.length > 0 ||
      clientProfile.address ||
      clientProfile.preferences
    );
  };

  const value = {
    user,
    clientProfile,
    loading,
    error,
    login,
    register,
    logout,
    updateProfile,
    createUser,
    hasCompleteAddress,
    
    // Client profile helpers
    getClientProfile,
    hasClientProfile,
    isReturningCustomer,
    fetchClientProfile: () => fetchClientProfile(token)
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 